/**
 * Codegen-typed time-entry projection shared by report surfaces.
 *
 * `time_entries` carries denormalized columns (`user_name`, `company_name`,
 * `ticket_title`, …) kept in sync by DB triggers, so report queries select
 * scalar columns directly — NO nested PostgREST joins (which return the
 * `T | T[] | null` union that forced the old `unwrapRelation` + `any` shims).
 * The field list is checked against the generated `Database` schema, so a
 * renamed/removed column is a compile error here instead of a silent runtime
 * blank.
 */
import type { Tables } from "@/lib/supabase/database.types";

/** Columns every report query selects from `time_entries`. */
export const REPORT_TIME_ENTRY_FIELDS = [
  "id",
  "user_id",
  "user_name",
  "company_id",
  "company_name",
  "contact_name",
  "ticket_id",
  "ticket_number",
  "ticket_title",
  "date_worked",
  "hours",
  "entry_type",
  "billable_rate",
  "is_approved",
  "sync_status",
  "description",
] as const satisfies readonly (keyof Tables<"time_entries">)[];

/** A strongly-typed, denormalized time-entry row as read by report queries. */
export type ReportTimeEntryRow = Pick<
  Tables<"time_entries">,
  (typeof REPORT_TIME_ENTRY_FIELDS)[number]
>;

/** Comma-joined select string for PostgREST `.select(...)`. */
export const REPORT_TIME_ENTRY_SELECT = REPORT_TIME_ENTRY_FIELDS.join(", ");

/**
 * CLIENT-SAFE projection (role = client_user). Selects ONLY the
 * work-transparency columns a client may see (CLIENT_USER_ACCESS_SCOPE).
 * Internal columns — `billable_rate`, `user_id`, `user_name`, `is_approved`,
 * `sync_status` (and `quickbooks_*`, raw times, audit timestamps) — are
 * deliberately NOT selected, so they never reach a client over the wire. RLS
 * still row-scopes which entries a client can read; this enforces the COLUMN
 * boundary RLS cannot. `company_name`/`contact_name`/`ticket_*` are denormalized
 * display LABELS, not internal data.
 */
export const REPORT_TIME_ENTRY_CLIENT_FIELDS = [
  "id",
  "company_id",
  "company_name",
  "contact_name",
  "ticket_id",
  "ticket_number",
  "ticket_title",
  "date_worked",
  "hours",
  "entry_type",
  "description",
] as const satisfies readonly (keyof Tables<"time_entries">)[];

/** A client-safe time-entry row (work-transparency columns only). */
export type ReportClientTimeEntryRow = Pick<
  Tables<"time_entries">,
  (typeof REPORT_TIME_ENTRY_CLIENT_FIELDS)[number]
>;

/** Comma-joined client-safe select string for PostgREST `.select(...)`. */
export const REPORT_TIME_ENTRY_CLIENT_SELECT =
  REPORT_TIME_ENTRY_CLIENT_FIELDS.join(", ");

/** `entry_type` values that count as billable; everything else is non-billable. */
export function isBillableEntry(entryType: ReportTimeEntryRow["entry_type"]): boolean {
  return entryType === "billable";
}

/** Safe numeric hours (the column is `number`, but guard against null/NaN). */
export function entryHours(row: Pick<ReportTimeEntryRow, "hours">): number {
  const h = typeof row.hours === "number" ? row.hours : Number(row.hours);
  return Number.isFinite(h) ? h : 0;
}
